import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../context/AuthContext.js';
import { LifeBuoy, PlusCircle, AlertCircle, MailCheck, ArrowRight } from 'lucide-react';
import TicketConversationModal from './TicketConversationModal.js';
import Table from './Table.js';
import Tag from './Tag.js';
import { ThemedSwal } from '../../views/AuthFlow';
import '../../styles/SoporteUsuario.css';

const SoporteUsuario = () => {
    const { currentUser } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [permissionError, setPermissionError] = useState(null);

    const functions = useMemo(() => getFunctions(undefined, 'us-central1'), []);
    const apiCallable = useMemo(() => httpsCallable(functions, 'api'), [functions]);

    const normalizeStatus = (value) => value ? value.toString().trim().toLowerCase() : '';
    const respondedTickets = useMemo(
        () => tickets.filter(ticket => normalizeStatus(ticket.status) === 'respondido'),
        [tickets]
    );

    const handleGoToTicket = useCallback((ticketId) => {
        const target = tickets.find(ticket => ticket.id === ticketId);
        if (target) {
            setSelectedTicket(target);
        }
    }, [tickets]);

    const formatDateTime = (value) => {
        if (!value) return 'N/A';
        const date = new Date(value);
        return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString('es-ES');
    };

    const refreshTickets = useCallback(async (options = { withSpinner: false }) => {
        if (!currentUser) return;
        if (!apiCallable) return;
        if (options.withSpinner) setLoading(true);
        try {
            const response = await apiCallable({ __fn: 'listUserSupportTickets' });
            const fetchedTickets = response?.data?.tickets || [];
            setTickets(fetchedTickets);
            setPermissionError(null);
        } catch (error) {
            console.error('Error fetching support tickets: ', error);
            setPermissionError('No pudimos cargar tus tickets. Verifica tus permisos o inténtalo nuevamente.');
        } finally {
            if (options.withSpinner) setLoading(false);
        }
    }, [apiCallable, currentUser]);

    useEffect(() => {
        if (!currentUser) return;

        let cancelled = false;

        const runInitialFetch = async () => {
            if (cancelled) return;
            await refreshTickets({ withSpinner: true });
        };

        runInitialFetch();
        const intervalId = setInterval(() => {
            if (!cancelled) {
                refreshTickets();
            }
        }, 5000);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [currentUser, refreshTickets]);

    const handleCreateTicket = async () => {
        // --- MEJORA: Usar el Swal temático para consistencia de diseño ---
        const { value: formValues } = await ThemedSwal.fire({
            title: 'Crear Nuevo Ticket de Soporte',
            html:
                '<div class="support-ticket-swal">' +
                    '<div class="support-ticket-swal__field">' +
                        '<label for="swal-subject">Asunto</label>' +
                        '<input id="swal-subject" class="support-ticket-swal__input" placeholder="Ej. Duda sobre mi retiro" />' +
                    '</div>' +
                    '<div class="support-ticket-swal__field">' +
                        '<label for="swal-department">Departamento</label>' +
                        '<select id="swal-department" class="support-ticket-swal__input">' +
                            '<option value="general">Consulta general</option>' +
                            '<option value="inversiones">Participaciones</option>' +
                            '<option value="equipo">Equipo de trabajo</option>' +
                            '<option value="retiros">Retiros</option>' +
                        '</select>' +
                    '</div>' +
                    '<div class="support-ticket-swal__field">' +
                        '<label for="swal-message">Mensaje</label>' +
                        '<textarea id="swal-message" class="support-ticket-swal__textarea" placeholder="Describe el contexto, IDs de acuerdo, fechas o montos relevantes..."></textarea>' +
                        '<small class="support-ticket-swal__hint">Mientras más contexto compartas, más rápido podremos ayudarte.</small>' +
                    '</div>' +
                '</div>',
            focusConfirm: false,
            confirmButtonText: 'Enviar Ticket',
            showCancelButton: true,
            width: '600px',
            didOpen: () => {
                const subjectInput = document.getElementById('swal-subject');
                subjectInput?.focus();
            },
            preConfirm: () => {
                return {
                    subject: document.getElementById('swal-subject').value,
                    department: document.getElementById('swal-department').value,
                    message: document.getElementById('swal-message').value
                }
            }
        });

        if (formValues) {
            const { subject, department, message } = formValues;
            if (!subject || !message) {
                ThemedSwal.fire('Error', 'El asunto y el mensaje son obligatorios.', 'error');
                return;
            }

            try {
                await apiCallable({
                    __fn: 'createSupportTicket',
                    subject: subject.trim(),
                    department,
                    message: message.trim(),
                });

                ThemedSwal.fire('¡Ticket Creado!', 'Tu consulta ha sido enviada. Un agente te responderá pronto.', 'success');
                refreshTickets();
            } catch (error) {
                console.error('Error creating ticket:', error);
                ThemedSwal.fire('Error', 'No se pudo crear el ticket. Inténtalo de nuevo.', 'error');
            }
        }
    };

    const columns = [
        { 
            header: 'Asunto', 
            accessor: item => (
                <button type="button" className="link-button-as-text" onClick={() => handleGoToTicket(item.id)}>{item.subject}</button>
            ) 
        },
        { header: 'Departamento', accessor: item => item.department },
        { 
            header: 'Estado', 
            accessor: item => {
                let color = 'gray';
                if (item.status === 'Abierto') color = 'green';
                if (item.status === 'Respondido') color = 'blue';
                return <Tag color={color}>{item.status}</Tag>;
            }
        },
        { header: 'Última Actualización', accessor: item => formatDateTime(item.lastUpdate) },
    ];

    return (
        <div className="main-panel">
            <div className="support-header">
                <h2 className="section-title">Centro de Soporte</h2>
                {/* --- CORRECCIÓN: Usar la clase de botón principal para consistencia --- */}
                <button className="login-button support-create-ticket-btn" onClick={handleCreateTicket}>
                    <PlusCircle size={20} /> Crear Nuevo Ticket
                </button>
            </div>
            <p>Aquí puedes gestionar tus consultas con nuestro equipo de soporte.</p>

            {respondedTickets.length > 0 && (
                <div className="support-response-alert">
                    <div className="support-response-alert__icon">
                        <MailCheck size={22} />
                    </div>
                    <div className="support-response-alert__body">
                        <strong>{respondedTickets.length === 1 ? 'Tienes una respuesta pendiente' : `Tienes ${respondedTickets.length} tickets con respuesta`}</strong>
                        <span>Revisa la conversación y responde para mantener tu caso activo.</span>
                    </div>
                    <button
                        type="button"
                        className="support-response-alert__cta"
                        onClick={() => handleGoToTicket(respondedTickets[0].id)}
                    >
                        Ver ticket
                        <ArrowRight size={16} />
                    </button>
                </div>
            )}

            {permissionError && (
                <div className="support-permission-alert">
                    <AlertCircle size={18} />
                    <span>{permissionError}</span>
                </div>
            )}

            {loading ? (
                <div className="support-ticket-placeholder">Cargando tus tickets de soporte...</div>
            ) : tickets.length > 0 ? (
                <Table columns={columns} data={tickets} />
            ) : (
                <div className="empty-state-text" style={{ textAlign: 'center', padding: '2rem' }}>
                    <LifeBuoy size={48} style={{ margin: '0 auto 1rem', color: '#ccc' }} />
                    <p>No tienes tickets de soporte. ¡Crea uno si necesitas ayuda!</p>
                </div>
            )}

            {selectedTicket && (
                <TicketConversationModal 
                    ticket={selectedTicket}
                    onClose={() => setSelectedTicket(null)}
                />
            )}
        </div>
    );
};

export default SoporteUsuario;
